// apps/admin/src/api/admin-roles.api.ts
//
// Phase 8.2 Wave 7 Task 51 — Admin Roles CRUD API helpers.
//
// Per CONTEXT.md
//   • Role CRUD with optimistic update + invalidation
//   • Permission catalog fetched from BE (no FE hardcoding)
//   • Custom sub-roles built from Admin permission pool
//   • Cannot delete role with active members (409 + member_count)
//   • Permission granularity: amount-gated keys (parent-child)
//   • RBAC colon-notation flat keys (module:action[:variant])
//
// Per INTERFACE-STANDARDS.md
//   • Success envelope: { ok: true, data, meta?, request_id? } — apiClient
//     already unwraps this at the response interceptor.
//   • Error envelope:   { ok: false, error: { code, message, user_message?, ... } }
//     → thrown as ApiError with INTERFACE-STANDARDS message priority.
//
// Endpoints (API-CONTRACTS task-48 — backend exposed in Wave 7):
//
//   GET    /api/v1/admin/roles?type=built_in|custom|all  → list roles
//   POST   /api/v1/admin/roles                           → create custom role
//   PATCH  /api/v1/admin/roles/:id                       → rename + perm diff
//   DELETE /api/v1/admin/roles/:id                       → remove (409 if member_count > 0)
//   GET    /api/v1/admin/permissions/catalog             → flat colon-key catalog
//
// vg-binding: PLAN:task-51
// vg-binding: P8.D-100
// vg-binding: P8.D-132
// vg-binding: P8.D-475
// vg-binding: P8.D-490
// vg-binding: P8.D-495
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import {
  ApiError,
  apiClient,
} from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Idempotency-Key helper. Fresh UUID v4 per mutation.
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

/** Row shape returned by GET /api/v1/admin/roles. */
export interface AdminRoleRow {
  /** Mongo ObjectId of the Role doc. */
  id: string;
  /** Slug — lowercase snake_case (e.g. `superadmin`, `accountant`, `finance_ops`). */
  code: string;
  /** Display label (Vietnamese canonical). */
  name: string;
  /** Optional admin-authored description (may be empty for built-in). */
  description?: string;
  /** Built-in (seeded) vs custom (admin-authored). */
  is_built_in: boolean;
  /** Number of flat colon-keys held by the role. */
  permission_count: number;
  /**
   * Active member count — used as a delete-guard: role with
   * member_count > 0 cannot be removed, BE returns 409 ROLE_HAS_MEMBERS.
   */
  user_count: number;
  /** Audit pointer — admin user id who created custom role; null for built-in. */
  created_by: string | null;
  /** ISO timestamp. */
  created_at: string;
}

/**
 * Detail shape returned by GET /api/v1/admin/roles/:id. Extends the list row
 * with the full flat colon-key grant set for the role editor.
 */
export interface AdminRoleDetail extends AdminRoleRow {
  /** Flat colon-keys held by the role. Order is BE-deterministic. */
  permissions: ReadonlyArray<string>;
  /** ISO timestamp of last update — used for optimistic concurrency hints. */
  updated_at?: string;
}

/** Body for POST /api/v1/admin/roles. */
export interface CreateAdminRoleBody {
  code: string;
  name: string;
  description?: string;
  /** Flat colon-keys. Validated server-side against parent-child. */
  permissions: string[];
}

/** Body for PATCH /api/v1/admin/roles/:id. All fields optional (partial update). */
export interface UpdateAdminRoleBody {
  name?: string;
  description?: string | null;
  permissions?: string[];
}

/**
 * Catalog entry returned by GET /api/v1/admin/permissions/catalog.
 *
 *  the FE MUST read this catalog from BE; FE-side enum mirrors
 * (apps/admin/src/lib/permissions.ts) are kept only as UX hint constants.
 */
export interface AdminPermissionCatalogEntry {
  /** Canonical colon-notation key. */
  key: string;
  /** Module slug — used by the tree UI for top-level grouping. */
  module: string;
  /** Vietnamese display label (canonical per CLAUDE.md). */
  label_vi: string;
  /** English fallback label. */
  label_en: string;
  /** Short rationale + introducing decision id. */
  description: string;
  /**
   * Optional parent key — set when this key is an `:unlimited` variant whose
   * grant requires the `:up_to_X` parent.
   */
  parent?: string | null;
}

// ---------------------------------------------------------------------------
// Public methods
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/roles — list built-in + custom roles. */
export async function listAdminRoles(
  type: 'built_in' | 'custom' | 'all' = 'all',
  opts?: { signal?: AbortSignal },
): Promise<AdminRoleRow[]> {
  // BE wraps list payload as `{ roles: [...] }` (admin-role.handler.ts
  // `envelopeSuccess(req, { roles })`); apiClient already unwrapped the
  // `data` envelope. Tolerate both shapes (raw array OR wrapped).
  const payload = await apiClient.get<
    AdminRoleRow[] | { roles: AdminRoleRow[] }
  >('/admin/roles', {
    params: { type },
    ...(opts?.signal ? { signal: opts.signal } : {}),
  });
  if (Array.isArray(payload)) return payload;
  return payload?.roles ?? [];
}

/** GET /api/v1/admin/roles/:id — fetch a role's full permission set for the editor. */
export async function getAdminRole(
  roleId: string,
  opts?: { signal?: AbortSignal },
): Promise<AdminRoleDetail> {
  return apiClient.get<AdminRoleDetail>(
    `/admin/roles/${encodeURIComponent(roleId)}`,
    opts?.signal ? { signal: opts.signal } : undefined,
  );
}

/** POST /api/v1/admin/roles — create a custom role from the Admin perm pool. */
export async function createAdminRole(
  body: CreateAdminRoleBody,
): Promise<{ id: string; code: string; audit_log_id?: string }> {
  return apiClient.post<{ id: string; code: string; audit_log_id?: string }>(
    '/admin/roles',
    body,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * PATCH /api/v1/admin/roles/:id — rename + permission diff update.
 *
 *  the BE enforces parent-child grant constraints (cannot grant
 * `:unlimited` without `:up_to_X`). The audit log records one entry per
 * field change + one entry per perm diff line (granted/revoked).
 */
export async function updateAdminRole(
  roleId: string,
  body: UpdateAdminRoleBody,
): Promise<{ id: string; updated_at: string; audit_log_id?: string }> {
  return apiClient.patch<{ id: string; updated_at: string; audit_log_id?: string }>(
    `/admin/roles/${encodeURIComponent(roleId)}`,
    body,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * DELETE /api/v1/admin/roles/:id — remove a custom role.
 *
 *  the BE returns 409 ROLE_HAS_MEMBERS with the live
 * `member_count` when the role still has active assignees. The FE must
 * surface that count so the operator can plan migration.
 */
export async function deleteAdminRole(roleId: string): Promise<void> {
  await apiClient.delete<void>(
    `/admin/roles/${encodeURIComponent(roleId)}`,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * GET /api/v1/admin/permissions/catalog — flat colon-key catalog.
 *
 *  this endpoint is the single source of truth for the
 * PermissionTreeView. The FE NEVER hardcodes the key list; it reads
 * from this endpoint at editor open.
 */
export async function getAdminPermissionsCatalog(
  opts?: { signal?: AbortSignal },
): Promise<AdminPermissionCatalogEntry[]> {
  return apiClient.get<AdminPermissionCatalogEntry[]>(
    '/admin/permissions/catalog',
    opts?.signal ? { signal: opts.signal } : undefined,
  );
}

// ---------------------------------------------------------------------------
// Error narrowing helpers
// ---------------------------------------------------------------------------

/**
 * True iff the ApiError is the 409 ROLE_HAS_MEMBERS / ROLE_DELETE_HAS_MEMBERS
 * envelope. The `details.member_count` payload (when present) lets the FE
 * delete-confirm dialog quote the exact count.
 */
export function isRoleHasMembersError(err: unknown): err is ApiError & {
  details: { member_count?: number };
} {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.code === 'ROLE_HAS_MEMBERS' ||
      err.code === 'ROLE_DELETE_HAS_MEMBERS')
  );
}

/**
 * Extract the `member_count` from a 409 ROLE_HAS_MEMBERS error if present.
 * Returns `null` when the BE did not include the count (defensive fallback).
 */
export function extractRoleMemberCount(err: unknown): number | null {
  if (!isRoleHasMembersError(err)) return null;
  const raw = (err.details as { member_count?: unknown } | undefined)?.member_count;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return raw;
}
